import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, inject } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DialogModule } from 'primeng/dialog';
import { FormComponent, ToastService } from '../../../../shared/components';
import {
  CitiesApiClientProvider,
  CreateCityModel,
  defaultPaginationParameters,
  ErrorOrError,
  handleApiErrors,
  ICreateCityRequest,
  IPaginationResponse,
  IStateResponse,
  Logger,
  StatesApiClientProvider,
  UpdateCityModel
} from '../../../../../common';
import { DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { Subscription, Observable } from 'rxjs';
import { FormInputConfig, ListValues } from '../../../../shared/interfaces/form-input-config';
import { ICreateCityResponse } from '../../../../../common/core/location-management/cities/dtos/responses/create.city.response';

@Component({
  selector: 'app-create-update-city',
  imports: [ReactiveFormsModule, DialogModule, CommonModule, FormComponent, TranslateModule],
  templateUrl: './create-update-city.component.html',
  styleUrl: './create-update-city.component.scss'
})
export class CreateUpdateCityComponent {
  private client = inject(CitiesApiClientProvider).getClient();
  private clientStates = inject(StatesApiClientProvider).getClient();
  private toastService = inject(ToastService);
  private cdr = inject(ChangeDetectorRef);
  private dialogRef = inject(DynamicDialogRef);
  private translate = inject(TranslateService);
  modalData = inject(DynamicDialogConfig);
  private subscriptions: Subscription[] = [];
  formConfigs: FormInputConfig[] = [];
  isLoadingAddEditItem = false;
  itemsFormValidationEmit = false;
  validationErrors = {};
  itemId: string = '';
  model: UpdateCityModel = CreateCityModel.createDefault();
  states: ListValues[] = [];

  ngOnInit(): void {
    this.itemId = this.modalData?.data?.item?.id ?? '';
    this.initializeItemData();
    this.getStates();
  }
  private initializeItemData(): void {
    const item = this.modalData?.data?.item;
    if (item) {
      this.model = UpdateCityModel.createFromItem(item);
    }
  }
  private initForm(): void {
    this.formConfigs = this.model.getFormConfig(this.states, this.modalData?.data?.visible, this.translate);
  }

  getStates(keyword?: string) {
    this.clientStates.get(defaultPaginationParameters).subscribe({
      next: (res: IPaginationResponse<IStateResponse>) => {
        this.processCountriesListResponse(res), this.initForm();
      },
      error: (errors: ErrorOrError[]) => handleApiErrors(errors, this.toastService)
    });
  }
  private processCountriesListResponse(response: IPaginationResponse<IStateResponse>): void {
    const items: IStateResponse[] = response.items ?? [];
    this.states = items.map((item: IStateResponse) => ({
      // label: item.englishName,
      label: this.translate.currentLang === 'ar' ? item.arabicName : item.englishName,
      value: item.id
    }));
  }

  formValuesChanged(data: any): void {
    Logger.debug('formValuesChanged:', data);
  }
  async submitForm(request: ICreateCityRequest): Promise<void> {
    Logger.debug('Submit form:', request, this.model);
    this.isLoadingAddEditItem = true;
    this.cdr.detectChanges();
    const request$: Observable<void | ICreateCityResponse> = this.model.id
      ? this.client.update(this.itemId, request)
      : this.client.create(request);
    request$.subscribe({
      next: (response) => this.handleActionSuccess(response),
      error: (errors) => this.handleActionError(errors),
      complete: () => this.finalizeAction()
    });
  }
  private handleActionSuccess(response: ICreateCityResponse | void): void {
    const successTitle = this.translate.instant('GENERAL.SUCCESS');
    const message = response
      ? this.translate.instant('GENERAL.SUCCESSFULLY_CREATED')
      : this.translate.instant('GENERAL.SUCCESSFULLY_UPDATED');
    this.toastService.showSuccess(successTitle, message);
    this.cdr.detectChanges();
    this.onClose(response ?? true);
  }
  private handleActionError(errors: ErrorOrError[]): void {
    Logger.debug('Action Error:', errors);
    if (this.toastService) {
      handleApiErrors(errors, this.toastService);
    }
    this.isLoadingAddEditItem = false;
  }
  private finalizeAction(): void {
    this.isLoadingAddEditItem = false;
    this.itemsFormValidationEmit = false;
  }
  emitValidation(): void {
    this.itemsFormValidationEmit = !this.itemsFormValidationEmit;
    this.cdr.detectChanges();
  }
  onClose(data?: boolean | ICreateCityResponse): void {
    this.dialogRef.close(data);
  }
  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => {
      if (sub && !sub.closed) {
        sub.unsubscribe();
      }
    });
  }
}
